import React, { useState, useEffect } from 'react';
import {
  getContentControlTags,
  insertRepeatingSection,
  selectContentControl,
  type ContentControlTag,
} from '../services/wordHelper';
import './RepeatingPlaceholders.css';

export function RepeatingPlaceholders() {
  const [controls, setControls] = useState<ContentControlTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadControls = async () => {
    setLoading(true);
    try {
      const all = await getContentControlTags();
      const repeating = all.filter((c) => c.tag.startsWith('REPEAT:'));
      setControls(repeating);
    } catch {
      setMessage({ type: 'error', text: 'Failed to scan document for repeating sections.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadControls();
  }, []);

  const handleAdd = async () => {
    if (!newTag.trim()) {
      setMessage({ type: 'error', text: 'Tag name is required.' });
      return;
    }

    setAdding(true);
    setMessage(null);

    try {
      await insertRepeatingSection(newTag.trim(), '');
      setMessage({ type: 'success', text: `Added repeating section: ${newTag}` });
      setNewTag('');
      await loadControls();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to insert repeating section.',
      });
    } finally {
      setAdding(false);
    }
  };

  const handleNavigate = async (tag: string) => {
    try {
      const found = await selectContentControl(tag);
      if (!found) {
        setMessage({ type: 'error', text: `Could not find control with tag: ${tag}` });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to navigate to control.',
      });
    }
  };

  return (
    <div className="dp-repeating-placeholders">
      <div className="dp-repeating-placeholders__header">
        <h3>Repeating Sections</h3>
        <button
          className="dp-repeating-placeholders__refresh-btn"
          onClick={loadControls}
          disabled={loading}
        >
          {loading ? 'Scanning...' : 'Refresh'}
        </button>
      </div>

      {controls.length === 0 && !loading && (
        <div className="dp-repeating-placeholders__empty">
          No repeating sections found in the document.
        </div>
      )}

      {controls.length > 0 && (
        <ul className="dp-repeating-placeholders__list">
          {controls.map((ctrl, index) => (
            <li key={`${ctrl.tag}-${index}`} className="dp-repeating-placeholders__item">
              <div className="dp-repeating-placeholders__item-info">
                <span className="dp-repeating-placeholders__prefix">REPEAT</span>
                <span className="dp-repeating-placeholders__tag">
                  {ctrl.tag.replace(/^REPEAT:/, '')}
                </span>
              </div>
              <button
                className="dp-repeating-placeholders__nav-btn"
                onClick={() => handleNavigate(ctrl.tag)}
              >
                Go to
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="dp-repeating-placeholders__add">
        <h4>Add Repeating Section</h4>
        <div className="dp-repeating-placeholders__add-row">
          <input
            type="text"
            className="dp-repeating-placeholders__input"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="e.g. co.directors"
          />
          <button
            className="dp-repeating-placeholders__add-btn"
            onClick={handleAdd}
            disabled={adding || !newTag.trim()}
          >
            {adding ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`dp-repeating-placeholders__message dp-repeating-placeholders__message--${message.type}`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
}
